import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../config/api';
import GoogleAuthButton from '../components/GoogleAuthButton';

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Helper function to create pending profile after authentication
  const createPendingProfile = async () => {
    const pendingProfile = localStorage.getItem('pendingProfile');
    if (pendingProfile) {
      try {
        const profileData = JSON.parse(pendingProfile);
        const response = await api.post('/profiles', profileData);
        
        if (response.data.success) {
          localStorage.removeItem('pendingProfile');
          toast.success('Profile created successfully!');
          navigate('/profiles');
          return true;
        }
      } catch (error) {
        console.error('Failed to create pending profile:', error);
        // Continue even if profile creation fails
      }
    }
    return false;
  };

  // Check for logout reason (account inactive) on mount
  useEffect(() => {
    const logoutReason = sessionStorage.getItem('logoutReason');
    const logoutMessage = sessionStorage.getItem('logoutMessage');
    const urlReason = searchParams.get('reason');

    if ((logoutReason === 'ACCOUNT_INACTIVE' || urlReason === 'inactive') && logoutMessage) {
      setAuthError('INACTIVE_ACCOUNT');
      toast.error(logoutMessage, { duration: 6000 });
      // Clear session storage after showing message
      sessionStorage.removeItem('logoutReason');
      sessionStorage.removeItem('logoutMessage');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateField = (name, value) => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    switch (name) {
      case 'email':
        if (!trimmedValue) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) return 'Enter a valid email address';
        return '';
      case 'password':
        if (!value) return 'Password is required';
        if (value.length < 6) return 'Password must be at least 6 characters';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const validationErrors = {};
    Object.entries(formData).forEach(([name, value]) => {
      const error = validateField(name, value);
      if (error) {
        validationErrors[name] = error;
      }
    });
    return validationErrors;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setAuthError('');
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...validationErrors }));
      setTouched((prev) => ({ ...prev, email: true, password: true }));
      toast.error('Please fix the highlighted fields');
      return;
    }

    setLoading(true);
    const result = await login(formData.email, formData.password);
    setLoading(false);

    if (result.success) {
      toast.success('Login successful!');
      
      // Check for pending profile data and create it
      const profileCreated = await createPendingProfile();
      if (!profileCreated) {
        navigate('/profiles');
      }
    } else {
      // Handle inactive account specifically
      if (result.message === 'ACCOUNT_INACTIVE') {
        const inactiveMessage = result.error || 'Your account is inactive. Please contact support for assistance.';
        setAuthError('INACTIVE_ACCOUNT');
        toast.error(inactiveMessage, { duration: 6000 });
      } else {
        const errorMessage = result.message?.toLowerCase().includes('invalid')
          ? 'Invalid credentials. Please check your email and password.'
          : result.message || 'Invalid credentials. Please check your email and password.';
        setAuthError(errorMessage);
        toast.error(errorMessage);
      }
    }
  };

  const showError = (field) => touched[field] && errors[field];

  const isGoogleEnabled = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex flex-col">
      <Header />
      <div className="container mx-auto px-4 py-16 flex-1">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <span className="text-4xl font-black tracking-tight leading-none select-none">
                <span className="text-[#ff405c]">my</span>
                <span className="text-[#5c3fd8]">STORY</span>
                <span className="text-[#ff405c]">me</span>
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
            <p className="text-gray-600">Sign in to continue creating magical stories</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            {isGoogleEnabled && (
              <div className="mb-8">
                <GoogleAuthButton
                  textVariant="signin_with"
                  onSuccess={async () => {
                    const profileCreated = await createPendingProfile();
                    if (!profileCreated) {
                      navigate('/profiles');
                    }
                  }}
                />
                <div className="flex items-center gap-3 text-gray-400 text-sm mt-6">
                  <span className="h-px flex-1 bg-gray-200" />
                  <span>Or sign in with email</span>
                  <span className="h-px flex-1 bg-gray-200" />
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none ${
                      showError('email') ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-purple-500'
                    }`}
                    placeholder="your@email.com"
                  />
                </div>
                {showError('email') && (
                  <p className="text-xs text-red-600 mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    className={`w-full pl-10 pr-12 py-3 border-2 rounded-lg focus:outline-none ${
                      showError('password') ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-purple-500'
                    }`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-600 transition-colors focus:outline-none"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {showError('password') && (
                  <p className="text-xs text-red-600 mt-1">{errors.password}</p>
                )}
              </div>

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm font-semibold text-purple-600 hover:text-purple-700">
                Forgot password?
              </Link>
            </div>

              {authError && (
                <div className={`border-2 rounded-lg p-4 flex items-start gap-3 ${
                  authError === 'INACTIVE_ACCOUNT' 
                    ? 'bg-amber-50 border-amber-300' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  {authError === 'INACTIVE_ACCOUNT' ? (
                    <>
                      <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-amber-900 mb-1">Account Inactive</p>
                        <p className="text-sm text-amber-800 mb-2">Your account is currently inactive. You cannot access your account at this time.</p>
                        <p className="text-sm font-medium text-amber-900">Please contact support to reactivate your account.</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-red-700 font-medium">{authError}</p>
                    </>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link to="/register" className="text-purple-600 hover:text-purple-700 font-semibold">
                  Sign up here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;

